import React from 'react';
import { useAuth } from '../context/AuthContext';

interface UsageCardProps {
  title: string;
  current: number;
  limit: number | 'unlimited';
  percentage: number | null;
  icon: string;
  upgradeMessage?: string;
}

function UsageCard({ title, current, limit, percentage, icon, upgradeMessage }: UsageCardProps) {
  const isUnlimited = limit === 'unlimited';
  const isNearLimit = percentage !== null && percentage >= 80;
  const isAtLimit = percentage !== null && percentage >= 100;

  return (
    <div className="panel p-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl ${isAtLimit ? 'bg-red-500/10' : isNearLimit ? 'bg-yellow-500/10' : 'bg-brand-500/10'}`}></div>

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-sm font-medium opacity-70 mb-1">{title}</div>
            <div className="flex items-baseline gap-2">
              <div className="text-3xl font-bold" style={{color: isAtLimit ? '#ef4444' : 'var(--cg-accent)'}}>
                {current}
              </div>
              {!isUnlimited && (
                <span className="text-sm opacity-60">/ {limit}</span>
              )}
              {isUnlimited && (
                <span className="text-sm font-medium text-brand-400">ilimitado</span>
              )}
            </div>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isAtLimit ? 'bg-red-500/10' : isNearLimit ? 'bg-yellow-500/10' : 'bg-brand-500/10'}`}>
            <i className={`${icon} ${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-brand-400'} text-xl`}></i>
          </div>
        </div>

        {/* Progress bar (apenas se não for unlimited) */}
        {!isUnlimited && percentage !== null && (
          <div className="mb-3">
            <div className="h-2 bg-slate-800/50 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${isAtLimit ? 'bg-gradient-to-r from-red-500 to-red-400' : isNearLimit ? 'bg-gradient-to-r from-yellow-500 to-yellow-400' : 'bg-gradient-to-r from-brand-500 to-brand-400'}`}
                style={{width: `${Math.min(percentage, 100)}%`}}
              ></div>
            </div>
          </div>
        )}

        {/* Status message */}
        {isAtLimit && upgradeMessage && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-start gap-2">
              <i className="fa-solid fa-exclamation-circle text-red-400 text-sm mt-0.5"></i>
              <div className="flex-1">
                <div className="text-xs font-medium text-red-400 mb-1">Limite atingido!</div>
                <div className="text-xs opacity-80">{upgradeMessage}</div>
              </div>
            </div>
          </div>
        )}

        {isNearLimit && !isAtLimit && (
          <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-2">
              <i className="fa-solid fa-exclamation-triangle text-yellow-400 text-sm mt-0.5"></i>
              <div className="text-xs opacity-80">Você está usando {percentage?.toFixed(0)}% do seu limite</div>
            </div>
          </div>
        )}

        {/* Unlimited info */}
        {isUnlimited && percentage !== null && (
          <div className="mt-3">
            <div className="text-xs opacity-60">
              Fair use: {current.toLocaleString()} usado
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function UsageDashboard() {
  const { user } = useAuth();

  // Se não há dados de subscription, não mostrar
  if (!user?.subscription) {
    return null;
  }

  const { subscription } = user;
  const { limits, usage, usagePercentages } = subscription;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Uso do Plano</h2>
        <p className="text-sm opacity-70">
          Acompanhe seu uso mensal de recursos
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Textos */}
        <UsageCard
          title="Textos Gerados"
          current={usage.textsGenerated}
          limit={limits.textsPerMonth}
          percentage={usagePercentages?.texts || null}
          icon="fa-solid fa-file-lines"
          upgradeMessage="Faça upgrade para continuar gerando textos"
        />

        {/* Chats */}
        <UsageCard
          title="Conversas de Chat"
          current={usage.chatsCreated}
          limit={limits.chatsPerMonth}
          percentage={usagePercentages?.chats || null}
          icon="fa-solid fa-comments"
          upgradeMessage="Faça upgrade para continuar usando o chat"
        />

        {/* Imagens */}
        <UsageCard
          title="Imagens Criadas"
          current={usage.imagesGenerated}
          limit={limits.imagesPerMonth}
          percentage={usagePercentages?.images || null}
          icon="fa-solid fa-images"
          upgradeMessage="Faça upgrade para criar mais imagens"
        />
      </div>

      {/* Plan info */}
      <div className="panel p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm opacity-70 mb-1">Plano Atual</div>
            <div className="text-xl font-bold">{subscription.planName}</div>
            {subscription.isCustomPlan && subscription.customNote && (
              <div className="text-xs opacity-60 mt-1">
                <i className="fa-solid fa-star text-yellow-400"></i> {subscription.customNote}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs opacity-70 mb-1">Próxima renovação</div>
            <div className="text-sm font-medium">
              {new Date(subscription.currentCycleEnd).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
